/* ==========================================================================
   Gallery Serpentine — Theme JavaScript
   Web Components for product page interactivity
   ========================================================================== */

/* ---------- Site Navigation (dropdown + mobile overlay) ---------- */
/*
  SiteNav Web Component wraps the entire <header> + mobile overlay.
  Responsibilities:
  - Desktop: Escape key closes any open dropdown; marks parent .is-open
    so CSS :hover-independent state works for keyboard-opened menus.
  - Mobile: hamburger button toggles the full-screen .mobile-nav overlay;
    manages aria-expanded / aria-hidden; traps focus inside overlay;
    mobile accordion toggles open/close children lists.
  - Respects prefers-reduced-motion for overlay transition.
*/

class SiteNav extends HTMLElement {
  connectedCallback() {
    this._hamburger  = this.querySelector('.site-header__hamburger');
    this._overlay    = this.querySelector('.mobile-nav');
    this._closeBtn   = this.querySelector('.mobile-nav__close');
    this._navItems   = Array.from(this.querySelectorAll('.nav-item--has-dropdown'));
    this._mobileToggles = Array.from(this.querySelectorAll('.mobile-nav__toggle'));

    this._bindHamburger();
    this._bindMobileToggles();
    this._bindDesktopKeyboard();
    this._bindOverlayEscape();
    this._bindMeganavHover();
  }

  /* ---- Hamburger / overlay open-close ---- */

  _bindHamburger() {
    if (!this._hamburger || !this._overlay) return;

    this._hamburger.addEventListener('click', () => this._toggleOverlay());
    this._closeBtn?.addEventListener('click', () => this._closeOverlay());

    // Close on backdrop click (click directly on the overlay bg, not its children)
    this._overlay.addEventListener('click', (e) => {
      if (e.target === this._overlay) this._closeOverlay();
    });
  }

  _toggleOverlay() {
    const isOpen = this._overlay.classList.contains('is-open');
    isOpen ? this._closeOverlay() : this._openOverlay();
  }

  _openOverlay() {
    this._overlay.classList.add('is-open');
    this._overlay.setAttribute('aria-hidden', 'false');
    this._hamburger.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden'; // prevent background scroll

    // Focus the close button
    requestAnimationFrame(() => this._closeBtn?.focus());

    // Focus trap
    this._overlay._focusTrapHandler = (e) => this._trapFocus(e);
    this._overlay.addEventListener('keydown', this._overlay._focusTrapHandler);
  }

  _closeOverlay() {
    this._overlay.classList.remove('is-open');
    this._overlay.setAttribute('aria-hidden', 'true');
    this._hamburger.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    // Return focus to hamburger
    this._hamburger?.focus();

    if (this._overlay._focusTrapHandler) {
      this._overlay.removeEventListener('keydown', this._overlay._focusTrapHandler);
      delete this._overlay._focusTrapHandler;
    }
  }

  _trapFocus(e) {
    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      this._overlay.querySelectorAll(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[hidden]'));

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ---- Mobile accordion (child list toggling) ---- */

  _bindMobileToggles() {
    this._mobileToggles.forEach((btn) => {
      btn.addEventListener('click', () => {
        const expanded = btn.getAttribute('aria-expanded') === 'true';
        const item = btn.closest('.mobile-nav__item');
        const children = item ? item.querySelector('.mobile-nav__children') : null;

        btn.setAttribute('aria-expanded', String(!expanded));
        if (children) {
          if (expanded) {
            children.setAttribute('hidden', '');
          } else {
            children.removeAttribute('hidden');
          }
        }
      });
    });
  }

  /* ---- Desktop keyboard navigation ---- */

  _bindDesktopKeyboard() {
    // Escape closes the currently focused dropdown
    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      const openItem = this.querySelector('.nav-item--has-dropdown.is-open');
      if (openItem) {
        openItem.classList.remove('is-open');
        openItem.querySelector('.nav-item__link')?.focus();
        return;
      }
      // Also close mobile overlay if open
      if (this._overlay?.classList.contains('is-open')) {
        this._closeOverlay();
      }
    });

    // Arrow-key navigation inside an open dropdown
    this._navItems.forEach((item) => {
      const dropdown = item.querySelector('.nav-dropdown');
      if (!dropdown) return;

      item.addEventListener('focusin', () => item.classList.add('is-open'));
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) item.classList.remove('is-open');
      });

      dropdown.addEventListener('keydown', (e) => {
        const links = Array.from(dropdown.querySelectorAll('.nav-dropdown__link'));
        const idx   = links.indexOf(document.activeElement);

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          links[(idx + 1) % links.length]?.focus();
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          links[(idx - 1 + links.length) % links.length]?.focus();
        }
      });
    });
  }

  /* ---- Meganav hover with 200ms close timeout (safe diagonal) ---- */

  _bindMeganavHover() {
    const items = Array.from(this.querySelectorAll('.nav-item--has-meganav'));
    items.forEach((item) => {
      let closeTimer = null;

      item.addEventListener('mouseenter', () => {
        clearTimeout(closeTimer);
        item.classList.add('is-open');
      });

      item.addEventListener('mouseleave', () => {
        closeTimer = setTimeout(() => item.classList.remove('is-open'), 200);
      });
    });

    // Escape closes any open meganav
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        items.forEach((item) => item.classList.remove('is-open'));
      }
    });
  }

  /* ---- Overlay Escape key ---- */

  _bindOverlayEscape() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._overlay?.classList.contains('is-open')) {
        this._closeOverlay();
      }
    });
  }
}

customElements.define('site-nav', SiteNav);

/* ---------- Product Media Gallery ---------- */

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.currentIndex = 0;
  }

  connectedCallback() {
    this.mediaItems = this.querySelectorAll('.product-gallery__media-item');
    this.thumbs = this.querySelectorAll('.product-gallery__thumb');
    this.prevBtn = this.querySelector('.product-gallery__nav--prev');
    this.nextBtn = this.querySelector('.product-gallery__nav--next');

    if (this.mediaItems.length === 0) return;

    this.thumbs.forEach((thumb, index) => {
      thumb.addEventListener('click', () => this.goTo(index));
    });

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.prev());
    }

    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.next());
    }

    // Initial setup — don't scroll the thumb strip on first connect,
    // it causes a visible page jump on mobile.
    this.goTo(0, { scrollThumb: false });
  }

  goTo(index, options = {}) {
    if (index < 0 || index >= this.mediaItems.length) return;
    const { scrollThumb = true } = options;

    this.mediaItems.forEach((item) => {
      item.classList.remove('is-active');
      const video = item.tagName === 'VIDEO' ? item : null;
      if (video) video.pause();
    });

    this.thumbs.forEach((thumb) => thumb.classList.remove('is-active'));

    const activeItem = this.mediaItems[index];
    activeItem.classList.add('is-active');

    if (activeItem.tagName === 'VIDEO') {
      activeItem.play().catch(() => {});
    }

    if (this.thumbs[index]) {
      this.thumbs[index].classList.add('is-active');
      if (scrollThumb) {
        this.thumbs[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
      }
    }

    this.currentIndex = index;
  }

  prev() {
    const newIndex = this.currentIndex > 0 ? this.currentIndex - 1 : this.mediaItems.length - 1;
    this.goTo(newIndex);
  }

  next() {
    const newIndex = this.currentIndex < this.mediaItems.length - 1 ? this.currentIndex + 1 : 0;
    this.goTo(newIndex);
  }
}

customElements.define('product-gallery', ProductGallery);


/* ---------- Brand Story Slideshow ---------- */
/* Silent slow-fade auto-advance. No controls, no indicators.
   Only activates when 2+ slides are present.
   Interval driven by data-interval attribute (milliseconds),
   populated from section schema via Liquid. */

class BrandStorySlideshow extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.brand-story__slide'));
    if (this.slides.length < 2) return; // single image — nothing to do

    this.current  = 0;
    this.interval = parseInt(this.dataset.interval, 10) || 6000;
    this._timer   = null;

    // Start only when the section enters the viewport
    this._observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this._start();
          } else {
            this._stop();
          }
        });
      },
      { threshold: 0.2 }
    );

    this._observer.observe(this);

    // Pause on reduced-motion preference
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    mq.addEventListener('change', (e) => {
      if (e.matches) {
        this._stop();
      } else {
        this._start();
      }
    });
  }

  disconnectedCallback() {
    this._stop();
    if (this._observer) this._observer.disconnect();
  }

  _start() {
    if (this._timer) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mq.matches) return;
    this._timer = setInterval(() => this._advance(), this.interval);
  }

  _stop() {
    if (this._timer) {
      clearInterval(this._timer);
      this._timer = null;
    }
  }

  _advance() {
    const next = (this.current + 1) % this.slides.length;
    this.slides[this.current].classList.remove('is-active');
    this.slides[next].classList.add('is-active');
    this.current = next;
  }
}

customElements.define('brand-story-slideshow', BrandStorySlideshow);


/* ---------- Editorial Slideshow ---------- */
/*
  Full-width atmospheric cross-fade slideshow.
  Supports: auto-advance, touch/swipe, keyboard (←/→), dot indicators,
  prefers-reduced-motion (freezes at first slide), IntersectionObserver
  pause when off-screen.
*/

class EditorialSlideshow extends HTMLElement {
  connectedCallback() {
    this.slides   = Array.from(this.querySelectorAll('.editorial-banner__slide'));
    this.dots     = Array.from(this.querySelectorAll('.editorial-banner__dot'));
    this.current  = 0;
    this.total    = this.slides.length;
    this._timer   = null;
    this._reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (this.total < 2) return;

    this._autoplay = this.dataset.autoplay !== 'false';
    this._interval = parseInt(this.dataset.interval, 10) || 5000;

    this._bindDots();
    this._bindSwipe();
    this._bindKeyboard();
    this._bindVisibility();
    this._bindReducedMotion();

    this._applyHeight();
  }

  disconnectedCallback() {
    this._stop();
    if (this._observer) this._observer.disconnect();
  }

  /* ── Slide control ─────────────────────────────────────────── */

  goTo(index) {
    if (index === this.current) return;

    this.slides[this.current].classList.remove('is-active');
    this.slides[this.current].setAttribute('aria-hidden', 'true');
    if (this.dots[this.current]) this.dots[this.current].classList.remove('is-active');
    if (this.dots[this.current]) this.dots[this.current].setAttribute('aria-selected', 'false');

    this.current = (index + this.total) % this.total;

    this.slides[this.current].classList.add('is-active');
    this.slides[this.current].setAttribute('aria-hidden', 'false');
    if (this.dots[this.current]) this.dots[this.current].classList.add('is-active');
    if (this.dots[this.current]) this.dots[this.current].setAttribute('aria-selected', 'true');
  }

  next() { this.goTo(this.current + 1); }
  prev() { this.goTo(this.current - 1); }

  /* ── Auto-advance ──────────────────────────────────────────── */

  _start() {
    if (!this._autoplay || this._reduced || this._timer) return;
    this._timer = setInterval(() => this.next(), this._interval);
  }

  _stop() {
    if (this._timer) { clearInterval(this._timer); this._timer = null; }
  }

  _restart() { this._stop(); this._start(); }

  /* ── Dot clicks ────────────────────────────────────────────── */

  _bindDots() {
    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => { this.goTo(i); this._restart(); });
    });
  }

  /* ── Touch / swipe ─────────────────────────────────────────── */

  _bindSwipe() {
    let startX = 0;
    let startY = 0;
    let dragging = false;

    this.addEventListener('touchstart', (e) => {
      startX   = e.touches[0].clientX;
      startY   = e.touches[0].clientY;
      dragging = true;
    }, { passive: true });

    this.addEventListener('touchend', (e) => {
      if (!dragging) return;
      dragging = false;
      const dx = e.changedTouches[0].clientX - startX;
      const dy = e.changedTouches[0].clientY - startY;
      // Only register horizontal swipe if primarily horizontal movement
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy) * 1.5) {
        if (dx < 0) this.next(); else this.prev();
        this._restart();
      }
    }, { passive: true });
  }

  /* ── Keyboard ──────────────────────────────────────────────── */

  _bindKeyboard() {
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') { this.next(); this._restart(); }
      if (e.key === 'ArrowLeft')  { this.prev(); this._restart(); }
    });
  }

  /* ── Pause when off-screen ─────────────────────────────────── */

  _bindVisibility() {
    this._observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => {
        if (entry.isIntersecting) this._start();
        else this._stop();
      }),
      { threshold: 0.2 }
    );
    this._observer.observe(this);
  }

  /* ── Reduced motion ────────────────────────────────────────── */

  _bindReducedMotion() {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    mq.addEventListener('change', (e) => {
      this._reduced = e.matches;
      if (e.matches) this._stop(); else this._start();
    });
  }

  /* ── Height from schema ─────────────────────────────────────── */

  _applyHeight() {
    // The section wrapper gets a data attribute we can read to apply
    // the correct height CSS variable via inline style.
    const section = this.closest('.editorial-banner-section') || this.parentElement;
    if (!section) return;

    const heightMap = {
      small:  '40vh',
      medium: '55vh',
      large:  '70vh',
      full:   '100vh',
    };

    // Height value is passed via data-height injected in Liquid below.
    const key = this.dataset.height || 'medium';
    const val = heightMap[key] || '55vh';
    this.style.setProperty('--editorial-height', val);
    this.style.height = val;
  }
}

customElements.define('editorial-slideshow', EditorialSlideshow);


/* ---------- Size Chart Drawer ---------- */

class SizeChartDrawer extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
  }

  connectedCallback() {
    this.drawer = this.querySelector('.size-chart-drawer');
    this.overlay = this.querySelector('.size-chart-drawer__overlay');
    this.closeBtn = this.querySelector('.size-chart-drawer__close');

    document.querySelectorAll('[data-size-chart-open]').forEach((trigger) => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', () => this.close());
    }

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });
  }

  open() {
    this.drawer.classList.add('is-open');
    this.overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    this.isOpen = true;
    this.drawer.setAttribute('aria-hidden', 'false');

    const focusable = this.drawer.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    if (focusable) focusable.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    this.isOpen = false;
    this.drawer.setAttribute('aria-hidden', 'true');

    const trigger = document.querySelector('[data-size-chart-open]');
    if (trigger) trigger.focus();
  }
}

customElements.define('size-chart-drawer', SizeChartDrawer);


/* ---------- Variant Picker ---------- */

class VariantPicker extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onVariantChange.bind(this));
    this.productData = JSON.parse(this.querySelector('[type="application/json"]')?.textContent || '{}');
    this.updateAvailability();
  }

  onVariantChange() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.getVariantFromOptions(selectedOptions);

    if (variant) {
      this.updatePrice(variant);
      this.updateAvailabilityBadge(variant);
      this.updateAddToCartButton(variant);
      this.updateUrl(variant);
      this.updateVariantInput(variant);
    }
  }

  getSelectedOptions() {
    const options = [];
    this.querySelectorAll('.variant-picker__group').forEach((group) => {
      const checked = group.querySelector('input:checked');
      if (checked) options.push(checked.value);
    });
    return options;
  }

  getVariantFromOptions(options) {
    if (!this.productData.variants) return null;
    return this.productData.variants.find((variant) => {
      return variant.options.every((opt, i) => opt === options[i]);
    });
  }

  updatePrice(variant) {
    const priceEl = document.querySelector('.product-info__price');
    if (!priceEl) return;

    const formatMoney = (cents) => {
      const format = window.GallerySerpentine?.moneyFormat || '${{amount}}';
      return format.replace('{{amount}}', (cents / 100).toFixed(2))
                    .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
                    .replace('{{amount_with_comma_separator}}', (cents / 100).toFixed(2).replace('.', ','));
    };

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceEl.innerHTML = `
        <span class="price--sale">${formatMoney(variant.price)}</span>
        <span class="price--compare">${formatMoney(variant.compare_at_price)}</span>
      `;
    } else {
      priceEl.innerHTML = `<span class="price--regular">${formatMoney(variant.price)}</span>`;
    }
  }

  updateAvailabilityBadge(variant) {
    const badge = document.querySelector('.product-availability');
    if (!badge) return;

    badge.classList.remove('product-availability--in-stock', 'product-availability--preorder', 'product-availability--sold-out');

    const statusEl = badge.querySelector('.product-availability__status-text');
    const shippingEl = badge.querySelector('.product-availability__shipping');

    if (variant.available) {
      // Preorder if: inventory not tracked at any location (atelier convention for MTO/preorder),
      // OR (tracked, qty <= 0, policy = continue selling).
      const inventoryUntracked = !variant.inventory_management;
      const oversellAllowed = badge.dataset.preorderEnabled === 'continue';
      const isPreorder = inventoryUntracked || (variant.inventory_quantity <= 0 && oversellAllowed);

      if (isPreorder) {
        badge.classList.add('product-availability--preorder');
        if (statusEl) statusEl.textContent = badge.dataset.preorderText || 'Pre-Order';
        if (shippingEl) shippingEl.innerHTML = `<strong>${badge.dataset.preorderShipping || 'Estimated dispatch in 4-6 weeks'}</strong>`;
      } else {
        badge.classList.add('product-availability--in-stock');
        if (statusEl) statusEl.textContent = badge.dataset.inStockText || 'In Stock';
        if (shippingEl) shippingEl.innerHTML = `<strong>${badge.dataset.inStockShipping || 'Ready to ship from Melbourne'}</strong>`;
      }
    } else {
      badge.classList.add('product-availability--sold-out');
      if (statusEl) statusEl.textContent = 'Sold Out';
      if (shippingEl) shippingEl.innerHTML = 'Join our waitlist to be notified when this returns.';
    }
  }

  updateAddToCartButton(variant) {
    const btn = document.querySelector('.product-form__add-to-cart');
    const btnText = btn?.querySelector('.btn-text');
    if (!btn || !btnText) return;

    if (variant.available) {
      btn.disabled = false;
      const inventoryUntracked = !variant.inventory_management;
      if (inventoryUntracked || variant.inventory_quantity <= 0) {
        btnText.textContent = btn.dataset.preorderText || 'Pre-Order';
      } else {
        btnText.textContent = btn.dataset.addText || 'Add to Cart';
      }
    } else {
      btn.disabled = true;
      btnText.textContent = btn.dataset.soldOutText || 'Sold Out';
    }
  }

  updateUrl(variant) {
    if (!variant) return;
    const url = new URL(window.location);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url.toString());
  }

  updateVariantInput(variant) {
    const input = document.querySelector('input[name="id"]');
    if (input) input.value = variant.id;
  }

  updateAvailability() {
    const firstChecked = this.querySelector('input:checked');
    if (firstChecked) {
      this.onVariantChange();
    }
  }
}

customElements.define('variant-picker', VariantPicker);


/* ---------- Quantity Selector ---------- */

class QuantitySelector extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('.quantity-selector__input');
    this.decreaseBtn = this.querySelector('[data-decrease]');
    this.increaseBtn = this.querySelector('[data-increase]');

    if (this.decreaseBtn) {
      this.decreaseBtn.addEventListener('click', () => this.update(-1));
    }

    if (this.increaseBtn) {
      this.increaseBtn.addEventListener('click', () => this.update(1));
    }
  }

  update(delta) {
    const currentVal = parseInt(this.input.value, 10) || 1;
    const min = parseInt(this.input.min, 10) || 1;
    const max = parseInt(this.input.max, 10) || 9999;
    const newVal = Math.max(min, Math.min(max, currentVal + delta));
    this.input.value = newVal;
  }
}

customElements.define('quantity-selector', QuantitySelector);


/* ---------- Product Form (Add to Cart) ---------- */

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(e) {
    e.preventDefault();

    const submitBtn = this.form.querySelector('[type="submit"]');
    const btnText = submitBtn?.querySelector('.btn-text');
    const originalText = btnText?.textContent;

    if (submitBtn.disabled) return;

    submitBtn.disabled = true;
    if (btnText) btnText.textContent = 'Adding...';

    const formData = new FormData(this.form);

    // Build line item properties (name="properties[Foo]")
    const properties = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value && String(value).trim() !== '') {
        properties[match[1]] = value;
      }
    }

    // Build cart-level attributes (name="attributes[Foo]")
    const attributes = {};
    for (const [key, value] of formData.entries()) {
      const match = key.match(/^attributes\[(.+)\]$/);
      if (match && value && String(value).trim() !== '') {
        attributes[match[1]] = value;
      }
    }

    const body = {
      id: parseInt(formData.get('id'), 10),
      quantity: parseInt(formData.get('quantity'), 10) || 1
    };
    if (Object.keys(properties).length > 0) body.properties = properties;

    try {
      // 1. If we have cart attributes, save them to the cart first
      if (Object.keys(attributes).length > 0) {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ attributes })
        });
      }

      // 2. Add the line item
      const response = await fetch(window.GallerySerpentine.routes.cart_add_url + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.description || 'Could not add item to cart');
      }

      if (btnText) btnText.textContent = 'Added!';
      document.dispatchEvent(new CustomEvent('cart:updated'));

      setTimeout(() => {
        submitBtn.disabled = false;
        if (btnText) btnText.textContent = originalText;
      }, 2000);

    } catch (err) {
      if (btnText) btnText.textContent = originalText;
      submitBtn.disabled = false;
      alert(err.message);
    }
  }
}

customElements.define('product-form', ProductForm);


/* ---------- Review Carousel ---------- */
/* Manual testimonials carousel. Renders a scrollable track of review
   cards with prev/next arrow navigation. Swipe supported on touch devices.
   Used when Judge.me is toggled off in the testimonials section schema. */

class ReviewCarousel extends HTMLElement {
  constructor() {
    super();
    this._track   = null;
    this._cards   = [];
    this._current = 0;
    this._perPage = 3;
    this._startX  = 0;
  }

  connectedCallback() {
    this._track = this.querySelector('.testimonials__track');
    this._cards = Array.from(this.querySelectorAll('.review-card'));

    if (!this._track || this._cards.length === 0) return;

    this._updatePerPage();
    this._bindNav();
    this._bindTouch();
    this._bindKeys();

    window.addEventListener('resize', () => {
      this._updatePerPage();
      this._goTo(this._current, false);
    });
  }

  _updatePerPage() {
    const w = window.innerWidth;
    if (w >= 1024)      this._perPage = 3;
    else if (w >= 600)  this._perPage = 2;
    else                this._perPage = 1;
  }

  _bindNav() {
    const prev = this.querySelector('.testimonials__nav-btn--prev');
    const next = this.querySelector('.testimonials__nav-btn--next');
    if (prev) prev.addEventListener('click', () => this._step(-1));
    if (next) next.addEventListener('click', () => this._step(1));
  }

  _bindTouch() {
    this._track.addEventListener('touchstart', (e) => {
      this._startX = e.touches[0].clientX;
    }, { passive: true });

    this._track.addEventListener('touchend', (e) => {
      const delta = this._startX - e.changedTouches[0].clientX;
      if (Math.abs(delta) > 48) this._step(delta > 0 ? 1 : -1);
    }, { passive: true });
  }

  _bindKeys() {
    this.setAttribute('tabindex', '0');
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this._step(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); this._step(1); }
    });
  }

  _step(dir) {
    const maxIndex = Math.max(0, this._cards.length - this._perPage);
    this._goTo(Math.min(Math.max(this._current + dir, 0), maxIndex));
  }

  _goTo(index, animate = true) {
    this._current = index;

    if (!animate) {
      this._track.style.transition = 'none';
    }

    const cardWidth = this._cards[0]?.offsetWidth ?? 0;
    const gap = parseInt(getComputedStyle(this._track).columnGap) || 24;
    this._track.style.transform = `translateX(-${index * (cardWidth + gap)}px)`;

    if (!animate) {
      void this._track.offsetHeight; // force reflow then restore transition
      this._track.style.transition = '';
    }

    // Update button disabled states
    const prev = this.querySelector('.testimonials__nav-btn--prev');
    const next = this.querySelector('.testimonials__nav-btn--next');
    const maxIndex = Math.max(0, this._cards.length - this._perPage);
    if (prev) prev.disabled = index === 0;
    if (next) next.disabled = index >= maxIndex;
  }
}

customElements.define('review-carousel', ReviewCarousel);


/* ---------- Collection Filters ---------- */
/*
  CollectionFilters Web Component wraps the collection page.
  Responsibilities:
  - Sort select: navigates to the new URL on change
  - Mobile filter drawer: open/close with focus trap and body scroll lock
  - Filter form submission: builds URL from checked filters + price range
  - Escape key closes drawer
  - Matches SiteNav / SizeChartDrawer drawer pattern
*/

class CollectionFilters extends HTMLElement {
  connectedCallback() {
    this._drawer   = this.querySelector('.collection-filter-drawer');
    this._overlay  = this.querySelector('[data-filter-overlay]');
    this._toggle   = this.querySelector('.collection-toolbar__filter-toggle');
    this._closeBtn = this.querySelector('[data-filter-close]');
    this._form     = this.querySelector('[data-filter-form]');
    this._sortSel  = this.querySelector('[data-sort-select]');
    this._isOpen   = false;

    this._bindSort();
    this._bindDrawer();
    this._bindForm();
    this._bindEscape();
  }

  /* ── Sort select ─────────────────────────────────────────── */

  _bindSort() {
    if (!this._sortSel) return;
    this._sortSel.addEventListener('change', () => {
      const url = new URL(window.location);
      url.searchParams.set('sort_by', this._sortSel.value);
      url.searchParams.delete('page');
      window.location.href = url.toString();
    });
  }

  /* ── Drawer open / close ─────────────────────────────────── */

  _bindDrawer() {
    if (!this._toggle || !this._drawer) return;
    this._toggle.addEventListener('click', () => this._openDrawer());
    this._closeBtn?.addEventListener('click', () => this._closeDrawer());
    this._overlay?.addEventListener('click', () => this._closeDrawer());
  }

  _openDrawer() {
    if (!this._drawer) return;
    this._drawer.classList.add('is-open');
    this._drawer.setAttribute('aria-hidden', 'false');
    this._overlay?.classList.add('is-open');
    this._toggle?.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    this._isOpen = true;

    requestAnimationFrame(() => this._closeBtn?.focus());

    this._drawer._focusTrapHandler = (e) => this._trapFocus(e);
    this._drawer.addEventListener('keydown', this._drawer._focusTrapHandler);
  }

  _closeDrawer() {
    if (!this._drawer) return;
    this._drawer.classList.remove('is-open');
    this._drawer.setAttribute('aria-hidden', 'true');
    this._overlay?.classList.remove('is-open');
    this._toggle?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    this._isOpen = false;

    this._toggle?.focus();

    if (this._drawer._focusTrapHandler) {
      this._drawer.removeEventListener('keydown', this._drawer._focusTrapHandler);
      delete this._drawer._focusTrapHandler;
    }
  }

  _trapFocus(e) {
    if (e.key !== 'Tab') return;

    const focusable = Array.from(
      this._drawer.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((el) => !el.closest('[hidden]') && el.offsetParent !== null);

    if (focusable.length === 0) return;

    const first = focusable[0];
    const last  = focusable[focusable.length - 1];

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  /* ── Filter form submit ──────────────────────────────────── */

  _bindForm() {
    if (!this._form) return;
    this._form.addEventListener('submit', (e) => {
      e.preventDefault();
      const formData = new FormData(this._form);
      const url = new URL(window.location.origin + window.location.pathname);

      // Preserve sort_by if set
      const currentUrl = new URL(window.location);
      if (currentUrl.searchParams.has('sort_by')) {
        url.searchParams.set('sort_by', currentUrl.searchParams.get('sort_by'));
      }

      for (const [key, value] of formData.entries()) {
        if (value !== '' && value !== null) {
          url.searchParams.append(key, value);
        }
      }

      window.location.href = url.toString();
    });
  }

  /* ── Escape key ──────────────────────────────────────────── */

  _bindEscape() {
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this._isOpen) {
        this._closeDrawer();
      }
    });
  }
}

customElements.define('collection-filters', CollectionFilters);


/* ---------- Cart Count Badge ---------- */
/* Listens for cart:updated events (dispatched by ProductForm after add-to-cart)
   and fetches /cart.js to update the header badge with the current item count. */

(function initCartBadge() {
  function updateBadge(count) {
    document.querySelectorAll('[data-cart-count]').forEach((badge) => {
      badge.textContent = count;
      badge.setAttribute('aria-label', count + ' items');
      if (count > 0) {
        badge.classList.remove('cart-count-badge--hidden');
      } else {
        badge.classList.add('cart-count-badge--hidden');
      }
    });
  }

  document.addEventListener('cart:updated', async () => {
    try {
      const response = await fetch('/cart.js', {
        headers: { 'Accept': 'application/json' }
      });
      if (response.ok) {
        const cart = await response.json();
        updateBadge(cart.item_count);
      }
    } catch (e) {
      /* silently fail — badge stays at previous count */
    }
  });
})();


/* ---------- Cart Items (cart page) ---------- */
/* Web component wrapping the cart page. Intercepts quantity changes and
   remove clicks, calls /cart/change.js with the Section Rendering API,
   and swaps in the freshly rendered section markup. Falls back to native
   form submission if JS fails or is disabled (see <noscript> in section). */

class CartItems extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.statusEl = this.querySelector('[data-cart-status]');

    this.addEventListener('change', this.onChange.bind(this));
    this.addEventListener('click', this.onClick.bind(this));
  }

  onChange(e) {
    const input = e.target.closest('[data-quantity-input]');
    if (!input) return;
    const key = input.dataset.key;
    const quantity = Math.max(0, parseInt(input.value, 10) || 0);
    this.updateLine(key, quantity, input);
  }

  onClick(e) {
    const remove = e.target.closest('[data-remove]');
    if (!remove) return;
    e.preventDefault();
    this.updateLine(remove.dataset.key, 0, remove);
  }

  setBusy(busy) {
    this.querySelectorAll('[data-quantity-input], [data-remove]').forEach((el) => {
      if (busy) {
        el.setAttribute('aria-busy', 'true');
        if (el.tagName === 'INPUT') el.disabled = true;
      } else {
        el.removeAttribute('aria-busy');
        if (el.tagName === 'INPUT') el.disabled = false;
      }
    });
  }

  setStatus(message) {
    if (this.statusEl) this.statusEl.textContent = message || '';
  }

  async updateLine(key, quantity, source) {
    if (!key) return;
    this.setBusy(true);
    this.setStatus('');

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity, sections: this.sectionId })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || data.message || 'Could not update cart');
      }

      const html = data.sections && data.sections[this.sectionId];
      if (html) {
        this.replaceContents(html);
      } else {
        // No section HTML returned — reload as a safe fallback.
        window.location.reload();
        return;
      }

      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      // Roll back the input value to the value the server last knew about.
      if (source && source.tagName === 'INPUT' && source.defaultValue) {
        source.value = source.defaultValue;
      }
      this.setStatus(err.message || 'Could not update cart. Please try again.');
      this.setBusy(false);
    }
  }

  replaceContents(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector('cart-items');
    if (!fresh) {
      window.location.reload();
      return;
    }

    // Preserve focus on the same input/link if it still exists in the new markup.
    const active = document.activeElement;
    const focusKey = active && active.dataset && active.dataset.key;
    const focusSelector = active && active.matches('[data-quantity-input]')
      ? '[data-quantity-input]'
      : (active && active.matches('[data-remove]') ? '[data-remove]' : null);

    this.innerHTML = fresh.innerHTML;
    this.statusEl = this.querySelector('[data-cart-status]');

    if (focusKey && focusSelector) {
      const target = this.querySelector(`${focusSelector}[data-key="${focusKey}"]`);
      if (target) target.focus();
    }
  }
}

customElements.define('cart-items', CartItems);


/* ---------- Homepage transparent nav → midnight on scroll ---------- */
/* Simple scroll event listener. On the homepage only, checks window.scrollY
   against 80% of the viewport height. Once the user scrolls past that point
   (i.e. the hero is mostly gone), .site-header--scrolled is added to the
   header element; when they scroll back up it is removed. */
(function initStickyNav() {
  if (!document.body.classList.contains('template-index')) return;

  const header = document.querySelector('.site-header');
  if (!header) return;

  const THRESHOLD = window.innerHeight * 0.8;

  function onScroll() {
    if (window.scrollY > THRESHOLD) {
      header.classList.add('site-header--scrolled');
    } else {
      header.classList.remove('site-header--scrolled');
    }
  }

  window.addEventListener('scroll', onScroll, { passive: true });

  // Run once on load in case the page is already scrolled (e.g. browser restore)
  onScroll();
})();

/* ---------- Show-on-scroll (reveal) ---------- */
/* Adds .is-visible to any element with class .reveal when it enters
   the viewport. CSS transitions defined in design-system.css handle
   the actual opacity/transform animation. Stagger via .reveal--delay-* */

(function initReveal() {
  if (!('IntersectionObserver' in window)) {
    // Fallback: just show everything immediately
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target); // fire once only
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -48px 0px' }
  );

  document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));

  // Re-scan after Shopify section renders in the Theme Editor
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('.reveal:not(.is-visible)').forEach((el) => observer.observe(el));
  });
})();

/* ──────────────────────────────────────────────────────────────────
   Search drawer — header icon → drop-down with predictive results
   ────────────────────────────────────────────────────────────────── */
(function initSearchDrawer() {
  const drawer = document.querySelector('[data-search-drawer]');
  if (!drawer) return;

  const toggleBtns = document.querySelectorAll('[data-search-toggle]');
  const closeBtns = drawer.querySelectorAll('[data-search-close]');
  const input = drawer.querySelector('[data-search-input]');
  const results = drawer.querySelector('[data-search-results]');

  let lastQuery = '';
  let abortController = null;
  let debounceTimer = null;

  function open() {
    drawer.hidden = false;
    // Force reflow so the transition runs
    drawer.offsetHeight;
    drawer.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    toggleBtns.forEach((b) => b.setAttribute('aria-expanded', 'true'));
    setTimeout(() => input && input.focus(), 50);
  }

  function close() {
    drawer.classList.remove('is-open');
    document.body.style.overflow = '';
    toggleBtns.forEach((b) => b.setAttribute('aria-expanded', 'false'));
    setTimeout(() => {
      drawer.hidden = true;
      if (results) results.innerHTML = '';
      if (input) input.value = '';
      lastQuery = '';
    }, 300);
  }

  toggleBtns.forEach((btn) => btn.addEventListener('click', open));
  closeBtns.forEach((btn) => btn.addEventListener('click', close));

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
  });

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function fetchPredictive(query) {
    if (abortController) abortController.abort();
    abortController = new AbortController();

    // resources[limit] is per-type when limit_scope=each (the default).
    // options[fields] only affects products; non-product types use their own defaults.
    const url = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection,page,article&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,variants.sku,vendor,tag`;

    return fetch(url, { signal: abortController.signal })
      .then((r) => r.json())
      .then((data) => renderResults(data, query))
      .catch((err) => {
        if (err.name !== 'AbortError') {
          results.innerHTML = '<p class="search-drawer__empty">Search unavailable. Try again in a moment.</p>';
        }
      });
  }

  function productItem(p) {
    const img = p.image
      ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(p.title)}" loading="lazy">`
      : '';
    const price = p.price
      ? `<div class="search-drawer__item-price">${p.price}</div>`
      : '';
    return `
      <li class="search-drawer__item">
        <a href="${escapeHtml(p.url)}">
          <span class="search-drawer__item-image">${img}</span>
          <div class="search-drawer__item-title">${escapeHtml(p.title)}</div>
          ${price}
        </a>
      </li>`;
  }

  function compactItem(r) {
    return `
      <li class="search-drawer__item">
        <a href="${escapeHtml(r.url)}">
          <div class="search-drawer__item-title">${escapeHtml(r.title)}</div>
        </a>
      </li>`;
  }

  function renderResults(data, query) {
    const r = (data && data.resources && data.resources.results) || {};
    const products = r.products || [];
    const collections = r.collections || [];
    const pages = r.pages || [];
    const articles = r.articles || [];

    const total = products.length + collections.length + pages.length + articles.length;
    if (!total) {
      results.innerHTML = `<p class="search-drawer__empty">No matches for &ldquo;${escapeHtml(query)}&rdquo;.</p>`;
      return;
    }

    const sections = [];

    if (products.length) {
      sections.push(`
        <p class="search-drawer__results-heading">Products</p>
        <ul class="search-drawer__list">${products.slice(0, 6).map(productItem).join('')}</ul>
      `);
    }
    if (collections.length) {
      sections.push(`
        <p class="search-drawer__results-heading">Collections</p>
        <ul class="search-drawer__list search-drawer__list--compact">${collections.slice(0, 4).map(compactItem).join('')}</ul>
      `);
    }
    if (pages.length) {
      sections.push(`
        <p class="search-drawer__results-heading">Pages</p>
        <ul class="search-drawer__list search-drawer__list--compact">${pages.slice(0, 4).map(compactItem).join('')}</ul>
      `);
    }
    if (articles.length) {
      sections.push(`
        <p class="search-drawer__results-heading">Journal</p>
        <ul class="search-drawer__list search-drawer__list--compact">${articles.slice(0, 4).map(compactItem).join('')}</ul>
      `);
    }

    sections.push(`<a class="search-drawer__viewall" href="/search?q=${encodeURIComponent(query)}&type=product,collection,page,article">View all results →</a>`);

    results.innerHTML = sections.join('');
  }

  if (input) {
    input.addEventListener('input', (e) => {
      const q = e.target.value.trim();
      if (q === lastQuery) return;
      lastQuery = q;
      clearTimeout(debounceTimer);
      if (q.length < 2) {
        results.innerHTML = '';
        return;
      }
      debounceTimer = setTimeout(() => fetchPredictive(q), 220);
    });
  }
})();
